import React from 'react';
import { formatDistanceToNow } from 'date-fns';

import { Icon, IconName, Button, Card, CardHeader, CardBody, Badge } from '@repo/ui';
import type {
  ArchivalConfiguration,
  ArchivalHealthInfo,
} from '@xtnd/web/features/archival/types/archivalType';

interface ExternalArchivalConfigCardProps {
  config: ArchivalConfiguration;
  healthInfo?: ArchivalHealthInfo;
  onEdit: () => void;
  onTest: () => void;
  onDelete: () => void;
  onHealthCheck: () => void;
  isHealthCheckLoading: boolean;
}

/**
 * Individual Archival Service Configuration Card
 *
 * Displays configuration details, health status, and action buttons
 * for a single external archival service (Azure Blob, S3, OneDrive)
 */
export const ExternalArchivalConfigCard: React.FC<ExternalArchivalConfigCardProps> = ({
  config,
  healthInfo,
  onEdit,
  onDelete,
}) => {
  // Service type icons and colors
  const serviceConfig = {
    'azure-blob': {
      icon: '☁️',
      name: 'Azure Blob Storage',
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-700/50',
    },
    s3: {
      icon: '📦',
      name: 'AWS S3',
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      borderColor: 'border-orange-200 dark:border-orange-700/50',
    },
    onedrive: {
      icon: '📁',
      name: 'OneDrive',
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-700/50',
    },
  }[config.serviceType] || {
    icon: '🔧',
    name: config.serviceType,
    color: 'bg-xtnd-dark-400',
    bgColor: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50',
    borderColor: 'border-xtnd-dark-100 dark:border-xtnd-dark-600/50',
  };

  // Health status configuration
  type HealthConfig = {
    iconName: IconName;
    color: string;
    bgColor: string;
    badge: 'success' | 'error' | 'secondary';
    text: string;
  };

  const healthStatus = healthInfo?.status || 'unknown';
  const healthConfig: HealthConfig = (
    {
      healthy: {
        iconName: 'HiCheckCircle',
        color: 'text-green-700 dark:text-green-300',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        badge: 'success',
        text: 'Healthy',
      },
      unhealthy: {
        iconName: 'HiXCircle',
        color: 'text-red-700 dark:text-red-300',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        badge: 'error',
        text: 'Issues Detected',
      },
      unknown: {
        iconName: 'HiClock',
        color: 'text-xtnd-dark-500 dark:text-xtnd-dark-200',
        bgColor: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50',
        badge: 'secondary',
        text: 'Unknown Status',
      },
    } as const
  )[healthStatus];

  // Check if credentials are expiring soon
  const isCredentialExpiring = healthInfo?.metadata?.credentialExpiryWarning === true;
  const credentialExpiryDate = healthInfo?.metadata?.credentialExpiryDate;

  // Format last check time
  const lastCheckTime = healthInfo?.lastCheckTime
    ? formatDistanceToNow(new Date(healthInfo.lastCheckTime), { addSuffix: true })
    : 'Never';

  return (
    <Card
      className={`transition-all duration-200 hover:shadow-lg ${serviceConfig.borderColor} border-l-4 w-full max-w-none`}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-lg ${serviceConfig.bgColor} flex items-center justify-center text-2xl`}
            >
              {serviceConfig.icon}
            </div>
            <div>
              <h3 className="font-semibold text-xtnd-dark dark:text-xtnd-white">
                {serviceConfig.name}
              </h3>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-200">{config.serviceId}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant={healthConfig.badge}>
              <Icon name={healthConfig.iconName} className="w-3 h-3 mr-1" />
              {healthConfig.text}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {/* Configuration Details */}
        <div className="space-y-3 mb-4">
          {/* Azure Blob specific details */}
          {config.serviceType === 'azure-blob' && config.config.type === 'azure-blob' && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-xtnd-dark-500 dark:text-xtnd-dark-200">Storage Account:</span>
                <span className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  {config.config.storageAccount}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-xtnd-dark-500 dark:text-xtnd-dark-200">Container:</span>
                <span className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  {config.config.containerName}
                </span>
              </div>
            </div>
          )}

          {/* S3 specific details */}
          {config.serviceType === 's3' && config.config.type === 's3' && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-xtnd-dark-500 dark:text-xtnd-dark-200">Bucket:</span>
                <span className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  {config.config.bucketName}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-xtnd-dark-500 dark:text-xtnd-dark-200">Region:</span>
                <span className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  {config.config.region}
                </span>
              </div>
            </div>
          )}

          {/* OneDrive specific details */}
          {config.serviceType === 'onedrive' && config.config.type === 'onedrive' && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-xtnd-dark-500 dark:text-xtnd-dark-200">Folder Path:</span>
                <span className="font-medium text-xtnd-dark dark:text-xtnd-white">
                  {config.config.folderPath}
                </span>
              </div>
            </div>
          )}

          {/* Common details */}
          <div className="flex justify-between text-sm">
            <span className="text-xtnd-dark-500 dark:text-xtnd-dark-200">Created:</span>
            <span className="font-medium text-xtnd-dark dark:text-xtnd-white">
              {formatDistanceToNow(new Date(config.createdAt), { addSuffix: true })}
            </span>
          </div>
        </div>

        {/* Health Information */}
        {healthInfo && (
          <div className={`p-3 rounded-lg ${healthConfig.bgColor} mb-4`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-100">
                Health Status
              </span>
              <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">
                Last check: {lastCheckTime}
              </span>
            </div>

            {healthInfo.errorMessage && (
              <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                {healthInfo.errorMessage}
              </p>
            )}

            {isCredentialExpiring && credentialExpiryDate && (
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200 mb-2">
                <Icon name="HiExclamationCircle" size="sm" />
                <span>
                  Credentials expire{' '}
                  {formatDistanceToNow(new Date(credentialExpiryDate), { addSuffix: true })}
                </span>
              </div>
            )}

            {healthInfo.metadata?.responseTimeMs && (
              <div className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">
                Response time: {healthInfo.metadata.responseTimeMs}ms
              </div>
            )}
          </div>
        )}

        {/* Warning Messages */}
        {isCredentialExpiring && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-200">
              <Icon name="HiExclamationCircle" size="md" className="flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Credentials Expiring Soon</p>
                <p className="text-xs">
                  Please update your credentials to avoid service interruption.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-4 border-t border-xtnd-dark-50 dark:border-xtnd-dark-600">
          {/* Mobile: Single row, no icons */}
          <div className="flex gap-1.5 md:hidden">
            <Button onClick={onEdit} variant="outline" size="sm" className="flex-1 min-w-0 px-2">
              <span className="truncate text-xs">Edit</span>
            </Button>

            <Button
              onClick={onDelete}
              variant="outline"
              size="sm"
              className="flex-1 min-w-0 px-2 text-red-700 hover:text-red-800 hover:border-red-400 dark:text-red-400 dark:hover:text-red-300 dark:hover:border-red-500"
            >
              <span className="truncate text-xs">Delete</span>
            </Button>
          </div>

          {/* Medium: 2x2 Grid with icons and text */}
          <div className="hidden md:grid lg:hidden grid-cols-2 gap-2">
            <Button onClick={onEdit} variant="outline" size="sm" className="w-full justify-start">
              <Icon name="HiPencil" size="sm" className="mr-2" />
              <span>Edit</span>
            </Button>

            <Button
              onClick={onDelete}
              variant="outline"
              size="sm"
              className="w-full justify-start text-red-700 hover:text-red-800 hover:border-red-400 dark:text-red-400 dark:hover:text-red-300 dark:hover:border-red-500"
            >
              <Icon name="HiTrash" size="sm" className="mr-2" />
              <span>Delete</span>
            </Button>
          </div>

          {/* Large+: Single row with icons and text */}
          <div className="hidden lg:flex gap-2">
            <Button onClick={onEdit} variant="outline" size="sm" className="flex-1 min-w-0">
              <Icon name="HiPencil" size="sm" className="mr-1.5" />
              <span className="truncate">Edit</span>
            </Button>

            <Button
              onClick={onDelete}
              variant="outline"
              size="sm"
              className="flex-1 min-w-0 text-red-700 hover:text-red-800 hover:border-red-400 dark:text-red-400 dark:hover:text-red-300 dark:hover:border-red-500"
            >
              <Icon name="HiTrash" size="sm" className="mr-1.5" />
              <span className="truncate">Delete</span>
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
